/**
 * Page /mes-alertes — espace administré pour consulter les alertes municipales
 * et gérer ses préférences de communication.
 *
 * Livraison L13.5 (21 mai 2026) — Dashboards administré différenciés.
 *
 * Cette page est l'équivalent côté citoyen de la page /grc/alertes (qui est
 * réservée aux pros pour la création/administration). Elle expose en lecture :
 *   - L'alerte active actuelle (si présente)
 *   - L'historique des alertes municipales (limité aux 50 dernières)
 *   - Un raccourci vers /compte pour gérer opt-in newsletter + push notifications
 *
 * Robustesse :
 *   - L'endpoint /api/grc/alertes peut être restreint aux pros côté backend.
 *     Si la list() renvoie 403, on retombe gracefully sur l'alerte active seule
 *     (qui est exposée à tous via /api/grc/alertes/active). Pas de plantage.
 *
 * Conforme à la roadmap consolidée §4.1 :
 *   📍 Mes signalements (déjà /mes-signalements)
 *   🔔 Mes alertes (cette page) — toujours visible, opt-in via /compte
 *   🎓 Scolaire & cantine (placeholder phase 4)
 *   🏘️ Mes demandes urbanisme (placeholder phase 5)
 *   🏛️ Mon association (placeholder phase 3)
 */

import { useEffect, useState } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { api, ApiException } from '@/lib/api';
import type { PoleInfoPayload } from '@/lib/api';
import { useAuth } from '@/lib/auth-context';
import {
  Card, Button, Badge, Alert, LogoMark, SectionEyebrow,
} from '@/components/ui';
import { SectionRepliable } from '@/components/ui/SectionRepliable';
import { DechetsSection } from '@/components/administre/DechetsSection';
import { RdvSection } from '@/components/administre/RdvSection';
import { CimetiereSection } from '@/components/administre/CimetiereSection';
import { CollecteSection } from '@/components/administre/CollecteSection';
import { ThemeToggle } from '@/components/ThemeToggle';
import { AppHeader } from '@/components/AppHeader';
import {
  Landmark, ArrowLeft, Bell, AlertTriangle, AlertOctagon, Info,
  ChevronRight, Inbox, Mail, Smartphone, Settings2, MessageSquare,
  UtensilsCrossed, CalendarDays, Clock, FileText, Download, MapPin, Phone,
  Trash2, BellRing, Search, ExternalLink,
} from 'lucide-react';
import type { GrcAlerte } from '@roronoa-collectivites/shared-types';
import { lienOfficielDemarche } from '@/lib/demarche-liens';

// ============================================================================
// Styles par niveau d'alerte (aligné sur ActiveAlerteBanner pour cohérence)
// ============================================================================

type NiveauStyle = {
  ring: string;
  bg: string;
  pillTone: 'success' | 'warning' | 'urgent' | 'neutral';
  Icon: typeof Info;
  label: string;
};

const NIVEAU_STYLE: Record<string, NiveauStyle> = {
  info: {
    ring: 'ring-success/30',
    bg: 'bg-success-tint/60',
    pillTone: 'success',
    Icon: Info,
    label: 'Information',
  },
  avis: {
    ring: 'ring-warning/30',
    bg: 'bg-warning-tint/60',
    pillTone: 'warning',
    Icon: Bell,
    label: 'Avis',
  },
  alerte: {
    ring: 'ring-urgent/30',
    bg: 'bg-urgent-tint/60',
    pillTone: 'urgent',
    Icon: AlertTriangle,
    label: 'Alerte',
  },
  crise: {
    ring: 'ring-urgent/40',
    bg: 'bg-ink/10',
    pillTone: 'urgent',
    Icon: AlertOctagon,
    label: 'Crise',
  },
};

// Formatage date relative simple
function formatDateRelative(iso: string): string {
  const date = new Date(iso);
  return date.toLocaleDateString('fr-FR', {
    weekday: 'short',
    day: 'numeric',
    month: 'short',
    year: 'numeric',
    hour: '2-digit',
    minute: '2-digit',
  });
}

// Jour d'un menu (date pure yyyy-mm-dd) — libellé « lundi 30 juin », zone Paris.
function formatJourMenu(dateStr: string): string {
  const [y, m, d] = dateStr.split('-').map(Number);
  const dt = new Date(Date.UTC(y!, (m! - 1), d!));
  return dt.toLocaleDateString('fr-FR', {
    weekday: 'long', day: 'numeric', month: 'long', timeZone: 'UTC',
  });
}

// Date d'un événement (timestamptz) en zone Europe/Paris.
function formatDateEvent(iso: string): string {
  return new Date(iso).toLocaleDateString('fr-FR', {
    weekday: 'short', day: 'numeric', month: 'short', timeZone: 'Europe/Paris',
  });
}

// ============================================================================
// Page
// ============================================================================

export function MesAlertesPage() {
  const navigate = useNavigate();
  const { user, tenant } = useAuth();

  const [activeAlerte, setActiveAlerte] = useState<GrcAlerte | null>(null);
  const [historique, setHistorique] = useState<GrcAlerte[]>([]);
  const [loadingActive, setLoadingActive] = useState(true);
  const [loadingHistorique, setLoadingHistorique] = useState(true);
  const [historiqueAccess, setHistoriqueAccess] = useState<'ok' | 'forbidden' | 'error'>('ok');
  const [errorMsg, setErrorMsg] = useState<string | null>(null);

  // Pôle info (migration 116) : services, démarches, menus, événements.
  const [poleInfo, setPoleInfo] = useState<PoleInfoPayload | null>(null);
  const [pdfBusy, setPdfBusy] = useState<string | null>(null);

  useEffect(() => {
    let cancelled = false;
    (async () => {
      try {
        const res = await api.infosCommune.get();
        if (!cancelled) setPoleInfo(res);
      } catch { /* non bloquant : le pôle info est complémentaire */ }
    })();
    return () => { cancelled = true; };
  }, []);

  async function ouvrirPdfDemarche(id: string) {
    setPdfBusy(id);
    try {
      const { url } = await api.infosCommune.demarchePdfUrl(id);
      window.open(url, '_blank', 'noopener');
    } catch { /* silencieux : bouton seulement présent si PDF existe */ }
    finally { setPdfBusy(null); }
  }

  // Charge l'alerte active (toujours accessible aux administrés)
  useEffect(() => {
    let cancelled = false;
    (async () => {
      try {
        const res = await api.grc.alertes.active();
        if (!cancelled) setActiveAlerte(res.alerte);
      } catch (err) {
        if (!cancelled && err instanceof ApiException) {
          // 401 = pas connecté, ne devrait pas arriver ici. On ignore.
        }
      } finally {
        if (!cancelled) setLoadingActive(false);
      }
    })();
    return () => { cancelled = true; };
  }, []);

  // Charge l'historique (peut être restreint aux pros côté backend → fallback graceful)
  useEffect(() => {
    let cancelled = false;
    (async () => {
      try {
        const res = await api.grc.alertes.list({ limit: 50 });
        if (!cancelled) {
          setHistorique(res.items);
          setHistoriqueAccess('ok');
        }
      } catch (err) {
        if (!cancelled && err instanceof ApiException) {
          if (err.status === 403) {
            setHistoriqueAccess('forbidden');
          } else if (err.status !== 401) {
            setHistoriqueAccess('error');
            setErrorMsg(err.message);
          }
        }
      } finally {
        if (!cancelled) setLoadingHistorique(false);
      }
    })();
    return () => { cancelled = true; };
  }, []);

  const greeting = user?.prenom ? `Bonjour ${user.prenom}` : null;

  return (
    <div className="min-h-screen text-ink">
      <AppHeader module="Infos, alertes & services" accent="grc" />

      {/* ===== Main ===== */}
      <main id="main-content" className="mx-auto max-w-3xl px-4 py-8 sm:px-6 sm:py-10 animate-fade-in">
        {/* Hero */}
        <SectionEyebrow as="div">Pôle info de la commune</SectionEyebrow>
        <h1 className="mt-1.5 font-display text-xl font-semibold tracking-r-tight text-ink sm:text-2xl">
          {greeting ? `${greeting}, voici les infos de votre commune` : 'Infos, alertes & services'}
        </h1>
        <p className="mt-2 text-sm text-ink-muted">
          Alertes municipales, menus de la cantine, vie de la commune, horaires des services et aide
          aux démarches. Touchez une rubrique pour la dérouler.
        </p>

        {/* ============================================================ */}
        {/* SECTION 1 : Alerte active                                     */}
        {/* ============================================================ */}
        <section className="mt-8" aria-labelledby="alerte-active-title">
          <h2 id="alerte-active-title" className="font-display text-base font-semibold text-ink mb-3">
            Alerte en cours
          </h2>

          {loadingActive ? (
            <Card variant="glass" padded>
              <div className="flex items-center gap-3 text-ink-muted">
                <span className="h-4 w-4 animate-spin rounded-full border-2 border-tech border-t-transparent" aria-hidden="true" />
                <span className="text-sm">Chargement…</span>
              </div>
            </Card>
          ) : activeAlerte ? (
            <ActiveAlerteCard alerte={activeAlerte} />
          ) : (
            <Card variant="glass" padded>
              <div className="flex items-center gap-3">
                <div className="flex h-10 w-10 flex-shrink-0 items-center justify-center rounded-r-lg bg-success-tint/70 text-success-fg" aria-hidden="true">
                  <Info className="h-5 w-5" />
                </div>
                <div className="min-w-0">
                  <div className="font-medium text-ink">Aucune alerte en cours</div>
                  <div className="text-sm text-ink-muted">
                    Vous serez prévenu·e dès qu'une alerte municipale sera publiée.
                  </div>
                </div>
              </div>
            </Card>
          )}
        </section>

        {/* ============================================================ */}
        {/* SECTION PÔLE INFO : menus, vie de la commune, horaires, démarches */}
        {/* Repliées par défaut. Rubriques agrégées affichées seulement si  */}
        {/* le module source est actif sur la commune.                      */}
        {/* ============================================================ */}
        {poleInfo && (
          <div className="mt-8 space-y-2">
            {/* En tête : prise de rendez-vous puis aide aux démarches (l'un après l'autre) */}
            {poleInfo.rdv && (
              <RdvSection
                data={poleInfo.rdv}
                onUpdated={(next) => setPoleInfo((prev) => (prev ? { ...prev, rdv: next } : prev))}
              />
            )}

            {poleInfo.demarches.length > 0 && (
              <SectionRepliable
                title="Aide aux démarches"
                icon={<FileText className="h-5 w-5 text-grc" aria-hidden="true" />}
                defaultOpen={false}
                right={<Badge variant="neutral" size="sm">{poleInfo.demarches.length} fiches</Badge>}
              >
                <ul className="space-y-2.5">
                  {poleInfo.demarches.map((d) => {
                    // Recette 20/08 : les liens mairie s'AJOUTENT au lien
                    // automatique, ils ne le remplacent plus. Une fiche
                    // « Passeport » où la mairie ajoute « acheter un timbre
                    // fiscal » perdait le lien ANTS de la pré-demande — le
                    // contraire de l'intention. Le lien détecté vient en tête
                    // (c'est LA démarche), les ajouts mairie suivent ; dédoublonné
                    // par URL au cas où la mairie a ressaisi le même.
                    const auto = lienOfficielDemarche(d.titre);
                    const liensAffiches = [
                      ...(auto && !d.liens.some((l) => l.url === auto.url) ? [auto] : []),
                      ...d.liens,
                    ];
                    return (
                    <li key={d.id} className="rounded-r-lg border border-border bg-surface/50 p-3.5">
                      <div className="flex items-start justify-between gap-3">
                        <div className="min-w-0">
                          <div className="font-medium text-ink">{d.titre}</div>
                          {d.texte && <p className="mt-1 text-sm text-ink-muted">{d.texte}</p>}
                          {d.pieces.length > 0 && (
                            <div className="mt-2 flex flex-wrap gap-1.5">
                              {d.pieces.map((p, i) => (
                                <span key={i} className="rounded-r-pill border border-border bg-bg-2 px-2 py-0.5 text-xs text-ink-muted">{p}</span>
                              ))}
                            </div>
                          )}
                          {liensAffiches.length > 0 && (
                            <div className="mt-2.5 flex flex-wrap gap-x-4 gap-y-1">
                              {liensAffiches.map((l, i) => (
                                <a
                                  key={i}
                                  href={l.url}
                                  target="_blank"
                                  rel="noopener noreferrer"
                                  className="inline-flex items-center gap-1.5 text-sm font-medium text-grc hover:underline"
                                >
                                  <ExternalLink className="h-3.5 w-3.5" aria-hidden="true" /> {l.label}
                                </a>
                              ))}
                            </div>
                          )}
                        </div>
                        {d.aPdf && (
                          <Button variant="ghost" size="sm" onClick={() => ouvrirPdfDemarche(d.id)} disabled={pdfBusy === d.id}>
                            <Download className="h-4 w-4" /> {pdfBusy === d.id ? '…' : 'PDF'}
                          </Button>
                        )}
                      </div>
                    </li>
                    );
                  })}
                </ul>
              </SectionRepliable>
            )}

            {/* Collecte des déchets (masquage auto si la commune n'a rien saisi) */}
            {poleInfo.dechets && (
              <DechetsSection
                data={poleInfo.dechets}
                onUpdated={(next) => setPoleInfo((prev) => (prev ? { ...prev, dechets: next } : prev))}
              />
            )}

            {/* Objets trouvés (masquage auto si aucun objet à réclamer) */}
            {poleInfo.objetsTrouves && poleInfo.objetsTrouves.length > 0 && (
              <SectionRepliable
                title="Objets trouvés"
                icon={<Search className="h-5 w-5 text-grc" aria-hidden="true" />}
                defaultOpen={false}
                right={<Badge variant="warning" size="sm">{poleInfo.objetsTrouves.length} à réclamer</Badge>}
              >
                <p className="mb-2 text-xs text-ink-dim">
                  Vous reconnaissez un objet ? Contactez la mairie pour le récupérer.
                </p>
                <ul className="space-y-2.5">
                  {poleInfo.objetsTrouves.map((o) => (
                    <li key={o.id} className="flex items-start gap-3 rounded-r-lg border border-border bg-surface/50 p-3">
                      {o.photoUrl && (
                        <img
                          src={o.photoUrl}
                          alt={o.libelle}
                          loading="lazy"
                          referrerPolicy="no-referrer"
                          className="h-14 w-14 flex-shrink-0 rounded-r-md border border-border object-cover"
                        />
                      )}
                      <div className="min-w-0">
                        <div className="font-medium text-ink">{o.libelle}</div>
                        {o.description && <p className="mt-0.5 text-sm text-ink-muted">{o.description}</p>}
                        <div className="mt-1 flex flex-wrap gap-x-4 gap-y-0.5 text-xs text-ink-dim">
                          <span>Trouvé le {formatJourMenu(o.dateTrouve)}</span>
                          {o.lieu && <span className="inline-flex items-center gap-1"><MapPin className="h-3 w-3" />{o.lieu}</span>}
                        </div>
                      </div>
                    </li>
                  ))}
                </ul>
              </SectionRepliable>
            )}

            {/* Cimetière (masquage auto si aucun cimetière et aucune demande) */}
            {poleInfo.cimetiere && (
              <CimetiereSection
                data={poleInfo.cimetiere}
                onUpdated={(next) => setPoleInfo((prev) => (prev ? { ...prev, cimetiere: next } : prev))}
              />
            )}

            {/* Points de collecte & déchetterie (masquage auto si rien saisi) */}
            {poleInfo.collecte && (
              <CollecteSection
                data={poleInfo.collecte}
                onUpdated={(next) => setPoleInfo((prev) => (prev ? { ...prev, collecte: next } : prev))}
              />
            )}

            {/* Menus cantine */}
            {poleInfo.modules.cantineActif && poleInfo.menusSemaine.length > 0 && (
              <SectionRepliable
                title="Menus de la cantine"
                icon={<UtensilsCrossed className="h-5 w-5 text-cantine" aria-hidden="true" />}
                defaultOpen={false}
                right={<Badge variant="success" size="sm">Cette semaine</Badge>}
              >
                <ul className="divide-y divide-border">
                  {poleInfo.menusSemaine.map((m) => (
                    <li key={m.date} className="py-2.5">
                      <div className="text-xs font-medium uppercase tracking-wide text-ink-dim">
                        {formatJourMenu(m.date)}
                      </div>
                      <div className="mt-0.5 text-sm text-ink">
                        {[m.entree, m.plat, m.accompagnement, m.laitage, m.dessert].filter(Boolean).join(' · ') || '—'}
                      </div>
                    </li>
                  ))}
                </ul>
              </SectionRepliable>
            )}

            {/* Vie de la commune (événements municipaux + associations) */}
            {poleInfo.evenements.length > 0 && (
              <SectionRepliable
                title="Vie de la commune"
                icon={<CalendarDays className="h-5 w-5 text-grc" aria-hidden="true" />}
                defaultOpen={false}
                right={<Badge variant="neutral" size="sm">{poleInfo.evenements.length} à venir</Badge>}
              >
                <ul className="space-y-2.5">
                  {poleInfo.evenements.map((e) => (
                    <li key={e.id} className="rounded-r-lg border border-border bg-surface/50 p-3">
                      <div className="flex flex-wrap items-center gap-2 font-medium text-ink">
                        <span>{e.titre}</span>
                        {e.source === 'mairie'
                          ? <Badge variant="grc" size="sm">Mairie</Badge>
                          : <Badge variant="asso" size="sm">{e.associationNom ?? 'Association'}</Badge>}
                      </div>
                      <div className="mt-0.5 text-sm text-ink-muted">
                        {[
                          e.dateEvent ? formatDateEvent(e.dateEvent) : null,
                          e.horaires,
                          e.lieu,
                        ].filter(Boolean).join(' · ')}
                      </div>
                    </li>
                  ))}
                </ul>
              </SectionRepliable>
            )}

            {/* Horaires des services */}
            {poleInfo.services.length > 0 && (
              <SectionRepliable
                title="Horaires des services"
                icon={<Clock className="h-5 w-5 text-grc" aria-hidden="true" />}
                defaultOpen={false}
                right={<Badge variant="neutral" size="sm">{poleInfo.services.length} services</Badge>}
              >
                <ul className="divide-y divide-border">
                  {poleInfo.services.map((s) => (
                    <li key={s.id} className="py-2.5">
                      <div className="font-medium text-ink">
                        {s.nom}
                        {/* Item 6.5 — `saisonnier` = le back a substitué les
                            horaires d'été. Sans ce badge, l'administré verrait
                            des horaires différents de ceux affichés en mairie
                            sans savoir pourquoi. */}
                        {s.saisonnier && <Badge variant="info" size="sm" className="ml-2">Horaires d'été</Badge>}
                      </div>
                      {s.horaires && <div className="mt-0.5 text-sm text-ink-muted">{s.horaires}</div>}
                      <div className="mt-1 flex flex-wrap gap-x-4 gap-y-0.5 text-xs text-ink-dim">
                        {s.adresse && <span className="inline-flex items-center gap-1"><MapPin className="h-3 w-3" />{s.adresse}</span>}
                        {s.telephone && <span className="inline-flex items-center gap-1"><Phone className="h-3 w-3" />{s.telephone}</span>}
                        {s.email && <span className="inline-flex items-center gap-1"><Mail className="h-3 w-3" />{s.email}</span>}
                      </div>
                      {s.note && <div className="mt-1 text-xs text-ink-dim italic">{s.note}</div>}
                    </li>
                  ))}
                </ul>
              </SectionRepliable>
            )}
          </div>
        )}

        {/* ============================================================ */}
        {/* SECTION 2 : Préférences de communication                     */}
        {/* ============================================================ */}
        <section className="mt-8" aria-labelledby="preferences-title">
          <h2 id="preferences-title" className="font-display text-base font-semibold text-ink mb-3">
            Mes préférences
          </h2>
          <Card variant="glass" padded>
            <div className="flex items-start gap-3">
              <div className="flex h-10 w-10 flex-shrink-0 items-center justify-center rounded-r-lg bg-grc-tint/70 text-grc-fg" aria-hidden="true">
                <Settings2 className="h-5 w-5" />
              </div>
              <div className="min-w-0 flex-1">
                <div className="font-medium text-ink">Comment souhaitez-vous être prévenu·e&nbsp;?</div>
                <p className="mt-1 text-sm text-ink-muted">
                  Vos préférences de communication (newsletter municipale par email et
                  notifications push) se règlent depuis votre compte, à un seul endroit.
                </p>
                <div className="mt-3 grid gap-2 sm:grid-cols-2">
                  <div className="flex items-center gap-2 text-sm text-ink-muted">
                    <Mail className="h-4 w-4 flex-shrink-0 text-grc" aria-hidden="true" />
                    <span>Newsletter par email</span>
                  </div>
                  <div className="flex items-center gap-2 text-sm text-ink-muted">
                    <Smartphone className="h-4 w-4 flex-shrink-0 text-grc" aria-hidden="true" />
                    <span>Notifications push</span>
                  </div>
                  <div className="flex items-center gap-2 text-sm text-ink-muted">
                    <MessageSquare className="h-4 w-4 flex-shrink-0 text-grc" aria-hidden="true" />
                    <span>Alertes SMS</span>
                  </div>
                </div>
                <div className="mt-4">
                  <Button
                    variant="primary"
                    size="sm"
                    onClick={() => navigate('/compte')}
                  >
                    Régler dans mon compte
                  </Button>
                </div>
              </div>
            </div>
          </Card>
        </section>

        {/* ============================================================ */}
        {/* SECTION 3 : Historique                                        */}
        {/* ============================================================ */}
        <section className="mt-8" aria-labelledby="historique-title">
          <h2 id="historique-title" className="font-display text-base font-semibold text-ink mb-3">
            Historique des alertes
          </h2>

          {loadingHistorique ? (
            <Card variant="glass" padded>
              <div className="flex items-center gap-3 text-ink-muted">
                <span className="h-4 w-4 animate-spin rounded-full border-2 border-tech border-t-transparent" aria-hidden="true" />
                <span className="text-sm">Chargement de l'historique…</span>
              </div>
            </Card>
          ) : historiqueAccess === 'error' ? (
            <Alert variant="error" icon={AlertTriangle}>
              {errorMsg ?? 'Impossible de charger l\'historique pour le moment.'}
            </Alert>
          ) : historiqueAccess === 'forbidden' ? (
            <Card variant="glass" padded>
              <div className="flex items-center gap-3">
                <div className="flex h-10 w-10 flex-shrink-0 items-center justify-center rounded-r-lg bg-surface/70 text-ink-muted" aria-hidden="true">
                  <Inbox className="h-5 w-5" />
                </div>
                <div className="min-w-0">
                  <div className="font-medium text-ink">Historique réservé à l'usage interne</div>
                  <div className="text-sm text-ink-muted">
                    Seule l'alerte en cours est consultable pour le moment. Vos préférences vous garantissent
                    de recevoir les alertes futures via email ou notification push.
                  </div>
                </div>
              </div>
            </Card>
          ) : historique.length === 0 ? (
            <Card variant="glass" padded>
              <div className="flex items-center gap-3">
                <div className="flex h-10 w-10 flex-shrink-0 items-center justify-center rounded-r-lg bg-surface/70 text-ink-muted" aria-hidden="true">
                  <Inbox className="h-5 w-5" />
                </div>
                <div className="min-w-0">
                  <div className="font-medium text-ink">Aucune alerte historisée</div>
                  <div className="text-sm text-ink-muted">
                    Aucun message n'a été diffusé par votre mairie pour le moment.
                  </div>
                </div>
              </div>
            </Card>
          ) : (
            <ul className="space-y-2.5">
              {historique.map((alerte) => (
                <li key={alerte.id}>
                  <HistoriqueAlerteRow alerte={alerte} />
                </li>
              ))}
            </ul>
          )}
        </section>
      </main>
    </div>
  );
}

// ============================================================================
// Sous-composant — Carte de l'alerte active mise en avant
// ============================================================================

function ActiveAlerteCard({ alerte }: { alerte: GrcAlerte }) {
  const style = NIVEAU_STYLE[alerte.niveau] ?? NIVEAU_STYLE.info!;
  const Icon = style.Icon;

  return (
    <div
      className={[
        'relative rounded-r-2xl border backdrop-blur-glass shadow-glass p-5',
        'ring-2',
        style.ring,
      ].join(' ')}
      style={{
        backgroundImage:
          'radial-gradient(220px 120px at 80% 0%, oklch(var(--c-grc) / 0.18), transparent 70%), ' +
          'linear-gradient(180deg, oklch(var(--c-surface) / 0.65), oklch(var(--c-bg-2) / 0.55))',
        borderColor: 'oklch(var(--c-border-strong) / 0.6)',
      }}
    >
      <div className="flex items-start justify-between gap-3">
        <div className="flex items-start gap-3 min-w-0">
          <div
            className={[
              'flex h-10 w-10 flex-shrink-0 items-center justify-center rounded-r-lg',
              style.bg,
            ].join(' ')}
            aria-hidden="true"
          >
            <Icon className="h-5 w-5" />
          </div>
          <div className="min-w-0 flex-1">
            <Badge variant={style.pillTone} size="sm" className="mb-2">
              {style.label}
            </Badge>
            <h3 className="font-display text-base font-semibold text-ink leading-tight">
              {alerte.titre}
            </h3>
            <p className="mt-1.5 text-sm text-ink-muted">
              {alerte.message}
            </p>
            {alerte.dateDebut && (
              <p className="mt-2 text-xs text-ink-subtle tabular-nums">
                Publiée le {formatDateRelative(alerte.dateDebut)}
              </p>
            )}
          </div>
        </div>
      </div>

      <div className="mt-4 flex justify-end">
        <Link
          to={`/mes-alertes/${alerte.id}`}
          className="inline-flex items-center gap-1.5 rounded-r-pill border border-border bg-surface/60 px-3 py-1.5 text-xs font-medium text-ink-muted backdrop-blur-glass hover:bg-surface/80 hover:text-ink transition-all focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-grc focus-visible:ring-offset-2 focus-visible:ring-offset-bg"
        >
          Voir le détail
          <ChevronRight className="h-3.5 w-3.5" aria-hidden="true" />
        </Link>
      </div>
    </div>
  );
}

// ============================================================================
// Sous-composant — Ligne d'historique (compacte)
// ============================================================================

function HistoriqueAlerteRow({ alerte }: { alerte: GrcAlerte }) {
  const style = NIVEAU_STYLE[alerte.niveau] ?? NIVEAU_STYLE.info!;
  const Icon = style.Icon;
  const isActive = alerte.statut === 'active';

  return (
    <Link
      to={`/mes-alertes/${alerte.id}`}
      className="block rounded-r-lg border border-border bg-surface/50 backdrop-blur-glass p-4 transition-all hover:bg-surface/70 hover:border-border-strong focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-grc focus-visible:ring-offset-2 focus-visible:ring-offset-bg"
    >
      <div className="flex items-start gap-3">
        <div
          className={[
            'flex h-9 w-9 flex-shrink-0 items-center justify-center rounded-r-lg',
            style.bg,
          ].join(' ')}
          aria-hidden="true"
        >
          <Icon className="h-4 w-4" />
        </div>
        <div className="min-w-0 flex-1">
          <div className="flex items-center gap-2 flex-wrap">
            <Badge variant={style.pillTone} size="sm">
              {style.label}
            </Badge>
            {isActive && (
              <Badge variant="warning" size="sm">
                en cours
              </Badge>
            )}
          </div>
          <h3 className="mt-1.5 font-medium text-ink leading-tight truncate">
            {alerte.titre}
          </h3>
          <p className="mt-1 text-xs text-ink-subtle tabular-nums">
            {alerte.dateDebut ? formatDateRelative(alerte.dateDebut) : '—'}
          </p>
        </div>
        <ChevronRight className="h-4 w-4 flex-shrink-0 text-ink-subtle mt-1" aria-hidden="true" />
      </div>
    </Link>
  );
}
